import type { Weapon, WeaponAttrPoolEntry } from '../data.js';

/**
 * Universal pool for the user-selectable 3rd weapon attribute slot.
 * `max` values are TU22.1 estimates — exact game-file numbers come in via per-weapon
 * `attrSlot3Pool` override during data backfill (Phase 2).
 */
export const WEAPON_ATTR_POOL: WeaponAttrPoolEntry[] = [
  { stat: 'dta', max: 6 },
  { stat: 'hsd', max: 10 },
  { stat: 'chd', max: 10 },
  { stat: 'chc', max: 10 },
  { stat: 'dth', max: 10 },
  { stat: 'ooc', max: 10 },
  { stat: 'reload', max: 12 },
  { stat: 'stability', max: 12 },
  { stat: 'accuracy', max: 12 },
  { stat: 'mag', max: 13 },
  { stat: 'rpm', max: 5 },
  { stat: 'swap', max: 15 },
  { stat: 'range', max: 24 },
];

/**
 * Builtin-stat → 3rd-slot-stat normalization. Some builtin stat names differ from
 * pool names (e.g. `mag_add` is flat-rounds vs pool's `mag` percent — they share
 * the same in-game category, so user shouldn't be able to stack them).
 */
const BUILTIN_STAT_NORMALIZE: Record<string, string> = {
  mag_add: 'mag',
};

/**
 * Effective pool for a weapon: per-weapon override (if any) minus stats already
 * granted by the weapon's locked `builtInMods` (avoids double-bonus on exotics)
 * and the slot-2 preset locked stat (a stat already on slot 2 cannot reappear
 * on slot 3 — the in-game recalibration rule).
 *
 * Pistols have no slot 2 in-game (researcher-confirmed): even though the data
 * file currently lists intrinsicAttrs[1] for pistols, we don't exclude it from
 * the slot-3 pool until the data layer is updated to drop it.
 */
export function poolForWeapon(weapon: Weapon | null | undefined): WeaponAttrPoolEntry[] {
  if (!weapon) return WEAPON_ATTR_POOL;
  const base = weapon.attrSlot3Pool ?? WEAPON_ATTR_POOL;
  const usedByBuiltIn = new Set<string>();
  for (const m of weapon.builtInMods ?? []) {
    if (m.stat) {
      const normalized = BUILTIN_STAT_NORMALIZE[m.stat] ?? m.stat;
      usedByBuiltIn.add(normalized);
    }
    if (m.slot === 'magazine') usedByBuiltIn.add('mag');
  }
  // Slot 2 (intrinsicAttrs[1]) is a fixed preset stat that may not repeat on slot 3.
  if (
    weapon.category !== 'pistol' &&
    weapon.intrinsicAttrs &&
    weapon.intrinsicAttrs.length >= 2
  ) {
    const slot2 = weapon.intrinsicAttrs[1].stat;
    const normalized = BUILTIN_STAT_NORMALIZE[slot2] ?? slot2;
    usedByBuiltIn.add(normalized);
  }
  return base.filter((p) => !usedByBuiltIn.has(p.stat));
}

/** Default attribute (the data-shipped suggestion sitting at intrinsicAttrs[2]). */
export function defaultSlot3(weapon: Weapon | null | undefined): { stat: string; value: number } | null {
  const ia = weapon?.intrinsicAttrs;
  if (!ia || ia.length < 3) return null;
  return { stat: ia[2].stat, value: ia[2].value };
}

/** Resolve the actual {stat, value} that should be applied for the 3rd slot. */
export function resolveSlot3(
  weapon: Weapon | null | undefined,
  selectedStat: string | null
): { stat: string; value: number } | null {
  if (!weapon) return null;
  const def = defaultSlot3(weapon);
  if (!selectedStat) return def;
  const pool = poolForWeapon(weapon);
  const hit = pool.find((p) => p.stat === selectedStat);
  if (hit) return { stat: hit.stat, value: hit.max };
  return def;
}
